import React, { FC, useState } from 'react';
import { ChevronDown, ChevronRight, HelpCircle, GalleryHorizontal, LayoutGrid } from 'lucide-react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@pisaka/ui';
import { FontCreatorType } from '../fontCreatorTypes';
import FontControlsContainer from './FontControlsContainer';
import SymbolsContainer from '../symbol/SymbolsContainer';
import { useUpdateFont } from './useFonts';

type PropsType = FontCreatorType;

const FontContainer: FC<PropsType> = ({
  fontId,
  fontName,
  canDownloadFont,
  canGenerateFont,
  canPaymentFont,
  isShowSymbols,
  symbols,
  alphabetCountryCode,
  isWrapSymbols,
}: FontCreatorType) => {
  const { t } = useTranslation();
  const { updateFont } = useUpdateFont();

  const [isShow, setIsShow] = useState(isShowSymbols);
  const [isWrap, setIsWrap] = useState(isWrapSymbols);

  const title = `${fontName} (${alphabetCountryCode.toUpperCase()})`;

  const drawAlphabetsSymbols = symbols.filter(({ symbolSVGView, symbolSVGContent }) => symbolSVGView && symbolSVGContent);
  const hasAllSymbols = drawAlphabetsSymbols.length === symbols.length;
  const showGenerateHint = canGenerateFont && !hasAllSymbols;

  const toggleShow = () => setIsShow((prev) => {
    const next = !prev;
    updateFont({ fontId, isShowSymbols: next });
    return next;
  });

  const toggleWrap = () => setIsWrap((prev) => {
    const next = !prev;
    updateFont({ fontId, isWrapSymbols: next });
    return next;
  });

  return (
    <div className="border-b pb-8 mb-8 last:border-0">
      <div className="flex items-center gap-2 mb-4">
        <button
          type="button"
          onClick={toggleShow}
          className="flex items-center justify-center w-7 h-7 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors flex-shrink-0"
        >
          {isShow ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        {isShow && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={toggleWrap}
                  className="flex items-center justify-center w-7 h-7 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors flex-shrink-0"
                >
                  {isWrap ? <LayoutGrid className="w-4 h-4" /> : <GalleryHorizontal className="w-4 h-4" />}
                </button>
              </TooltipTrigger>
              <TooltipContent>
                {isWrap ? 'Развернуть список' : 'Свернуть список'}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        <h2 className="text-base font-semibold">{title}</h2>
        <FontControlsContainer
          canDownloadFont={canDownloadFont}
          canGenerateFont={canGenerateFont}
          canPaymentFont={canPaymentFont}
          symbols={symbols}
          alphabetCountryCode={alphabetCountryCode}
          fontId={fontId}
          fontName={fontName}
        />
        {showGenerateHint && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help flex-shrink-0" />
              </TooltipTrigger>
              <TooltipContent>
                <p>{t('fontCreator.generateHint')}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {isShow && (
        <div className={classNames('flex gap-2', isWrap ? 'flex-nowrap overflow-x-auto pb-2' : 'flex-wrap')}>
          <SymbolsContainer fontId={fontId} symbols={symbols} canGenerateFont={canGenerateFont} />
        </div>
      )}
    </div>
  );
};

export default FontContainer;
